// src/pages/ActorsGallery.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PERSONAS_DATA } from '../data/personas_data';
import PersonaModal from '../components/PersonaModal';
import { Lock } from 'lucide-react';

const ActorsGallery = () => {
  const [unlockedPersonas, setUnlockedPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Back to top
  const handleBackToTop = () => {
    if (typeof window === 'undefined') return;

    const el =
      document.scrollingElement ||
      document.documentElement ||
      document.body;

    el.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // Load unlocked status from localStorage
  useEffect(() => {
    const stored = JSON.parse(
      localStorage.getItem('innergem_unlocked_personas') || '[]'
    );
    setUnlockedPersonas(stored);
  }, []);

  // ✨ 新增：排序辅助函数 (解析 "20 · Shame" 中的 20)
  const getEnergyLevel = (persona) => {
    // 提取字符串开头的数字，处理 "700+" 这种情况
    const levelStr = persona.energy.split(' ')[0].replace('+', '');
    return parseInt(levelStr, 10) || 0;
  };

  const personasList = Object.values(PERSONAS_DATA);

  // ✨ 按能量值从高到低排序
  const survivalCrew = personasList
    .filter((p) => p.group === 'survival')
    .sort((a, b) => getEnergyLevel(b) - getEnergyLevel(a)); // High -> Low

  const creativeCrew = personasList
    .filter((p) => p.group === 'creative')
    .sort((a, b) => getEnergyLevel(b) - getEnergyLevel(a)); // High -> Low

  const CardItem = ({ persona }) => {
    const isUnlocked = unlockedPersonas.includes(persona.name);
    const isZh = (i18n?.language || '').toLowerCase().startsWith('zh');
    const imgSrc = isZh && persona.imageZh ? persona.imageZh : persona.image;

    return (
      <div
        onClick={() => isUnlocked && setSelectedPersona(persona)}
        className={`relative aspect-[2/3] rounded-2xl overflow-hidden shadow-md transition-all duration-300 border border-gray-100 ${
          isUnlocked
            ? 'cursor-pointer hover:scale-105 hover:shadow-xl bg-white'
            : 'cursor-not-allowed bg-slate-100'
        }`}
      >
        {isUnlocked ? (
          <img
            src={imgSrc}
            alt={persona.englishName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center opacity-40">
            {/* Large Lock Icon */}
            <Lock className="w-16 h-16 mb-4 text-gray-500" strokeWidth={1.5} />
            {/* Locked Text */}
            <span className="text-base font-black text-gray-500 uppercase tracking-[0.2em]">
              {t('personas.locked', { defaultValue: 'Locked' })}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/main')}
            className="inline-flex items-center rounded-full border px-3 py-1.5 text-xs border-border text-muted-foreground hover:bg-primary/5 hover:text-primary transition-colors"
          >
            {t('common.back', { defaultValue: 'Back' })}
          </button>
          <div className="text-center flex-1">
            <h1 className="text-4xl font-bold text-primary mb-2">{t('nav.innerCast', { defaultValue: 'Inner Cast' })}</h1>
            <p className="text-muted-foreground">
              {t('personas.subtitle', { defaultValue: 'Collect all 15 persona fragments to reclaim your wholeness.' })}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mt-6 flex items-center gap-3">
        <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden shadow-inner">
          <div
            className="h-full bg-purple-600 transition-all duration-1000 ease-out rounded-full"
            style={{ width: `${(unlockedPersonas.length / 15) * 100}%` }}
          ></div>
        </div>
        <span className="text-xs font-bold text-purple-700 min-w-[3rem] text-right">
          {unlockedPersonas.length}/15
        </span>
      </div>

      {/* Creative Crew Section (High Energy First) */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-purple-800 mb-4 flex items-center gap-2">
          ✨ {t('personas.creative', { defaultValue: 'Creative Crew' })}
          <span className="text-[10px] font-medium text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-100 uppercase tracking-wide">
            {t('personas.highVibe', { defaultValue: 'High Vibe' })}
          </span>
        </h2>
        <div className="grid grid-cols-3 gap-4">
          {creativeCrew.map((p) => (
            <CardItem key={p.id} persona={p} />
          ))}
        </div>
      </section>

      {/* Survival Crew Section (Lower Energy) */}
      <section>
        <h2 className="text-xl font-bold text-slate-700 mb-4 flex items-center gap-2">
          🛡️ {t('personas.survival', { defaultValue: 'Survival Crew' })}
          <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200 uppercase tracking-wide">
            {t('personas.protection', { defaultValue: 'Protection' })}
          </span>
        </h2>
        <div className="grid grid-cols-3 gap-4">
          {survivalCrew.map((p) => (
            <CardItem key={p.id} persona={p} />
          ))}
        </div>
      </section>

      {/* Modal */}
      {selectedPersona && (
        <PersonaModal
          persona={selectedPersona}
          onClose={() => setSelectedPersona(null)}
          isUnlocked={true}
        />
      )}

      {/* 底部 Back to top 按钮 */}
      <div className="mt-8 flex justify-center">
        <button
          type="button"
          onClick={handleBackToTop}
          className="mt-6 mx-auto flex items-center justify-center rounded-full border px-4 py-2 text-xs text-primary border-primary/40 hover:bg-primary/5 transition-colors"
        >
          {t('gems.backToTop', { defaultValue: 'Back to top' })}
        </button>
      </div>
    </div>
  );
};

export default ActorsGallery;
